import { getUnixTime } from 'date-fns';
import { MouseEvent, useRef, useState } from 'react';
import { ReferenceArea } from 'recharts';

import { segmentTrack } from '@/hooks/analytics/useTracking';
import { useInsightsPageContext } from '@/pages/Insights/Context/InsightsPageContext';
import { mergeQueryParams } from '@/utils/queryParams';

interface UseSelectionConfig {
  widgetId: string;
}

// recharts v3 removed the internal `recharts/types/chart/generateCategoricalChart` entry point
// (and its `CategoricalChartFunc`). Chart.tsx forwards the chart state to these handlers with
// `activeLabel` already coerced to a string, so this hook only types the field it reads.
type ChartSelectionState = { activeLabel?: string };
type ChartSelectionHandler = (state: ChartSelectionState | undefined, event?: MouseEvent<SVGGraphicsElement>) => void;

const useSelection = ({ widgetId }: UseSelectionConfig) => {
  const [x1, setX1] = useState<string | undefined>(undefined);
  const [x2, setX2] = useState<string | undefined>(undefined);
  const { pageId } = useInsightsPageContext();

  const usageRef = useRef(false);

  const [cursorDisplaced, setCursorDisplaced] = useState(false);

  const onMouseDown: ChartSelectionHandler = (e) => {
    if (!e) return;
    setX1(e.activeLabel);
    setX2(e.activeLabel);
  };

  const onMouseMove: ChartSelectionHandler = (e) => {
    if (!e) return;
    if (x1) {
      setX2(e.activeLabel);
      if (x2 && x1 !== x2) setCursorDisplaced(true);
    }
  };

  const onMouseUp: ChartSelectionHandler = (e) => {
    if (!e) return;
    setX2(e.activeLabel);

    const isValidSelection = x1 && x2 && ((cursorDisplaced && x1 !== x2) || !cursorDisplaced);

    if (isValidSelection) {
      if (!usageRef.current) {
        segmentTrack('Insights Chart Zoomed', {
          page_id: pageId,
          widget_id: widgetId,
        });
        usageRef.current = true;
      }

      mergeQueryParams({
        set: [
          {
            type: 'dateFrom',
            value: getUnixTime(Math.min(+x1, +x2)).toString(),
          },
          {
            type: 'dateTo',
            value: getUnixTime(Math.max(+x1, +x2)).toString(),
          },
          { type: 'duration', value: 'FIXED' },
        ],
      });
    }

    setCursorDisplaced(false);
    setX1(undefined);
    setX2(undefined);
  };

  return {
    x1,
    x2,
    referenceArea: x1 && x2 ? <ReferenceArea x1={x1} x2={x2} /> : null,
    onMouseDown,
    onMouseMove,
    onMouseUp,
  };
};

export default useSelection;
